export type SugarMessageType = 'error' | 'warning' | 'info' | 'success'

export interface SugarMessage {
  type: SugarMessageType
  message: string
  // MIQ-2641: present on every per-item `type: error` from
  // POST /MetrixEnroll/attendance/process, so a rejection can be shown on the row
  // that failed. Absent on messages from every other endpoint.
  enrollmentId?: string | null
}

export interface SugarRequest {
  cognitoId: string
}

export interface SugarResponse<T> {
  data: T
  messages?: SugarMessage[]
  success: boolean
  // Optional error envelope used by MIQ-2464+ endpoints. Older endpoints still
  // surface failures via `messages: [{ type: 'error', message }]`.
  error?: {
    message: string
    code?: string
  }
}

export interface SugarResponsePagination {
  currentPage: number
  limit: number
  totalItems: number
  totalPages: number
}

export type SugarPaginationParams = {
  page?: number
}

export interface SugarPaginatedResponse<T> extends SugarResponse<T[]> {
  pagination: SugarResponsePagination
}

export interface SugarProfile {
  id: string
  firstName: string
  lastName: string
  email: string
  cognitoId: string
}

export interface SugarProfileInput {
  email?: string
  firstName?: string
  lastName?: string
}

export interface SugarSite {
  id: string
  name: string
  type?: string
  programYears: SugarProgramYear[]
  // Legacy site-level flag; read only by the older-backend fallback when
  // SugarAttendanceSheet.attendanceGated is absent.
  publishedTuitionRatesComplete: boolean
}

export type SugarOptionName =
  | 'applicationStatus'
  | 'applicationStatusDetails'
  | 'curriculum'
  | 'documentCategory'
  | 'documentSubcategory'
  | 'enrollmentStatus'
  | 'languagesSpoken'
  | 'operatingDays'
  | 'participationTypes'
  | 'paymentMonth'
  | 'programAgesServed'
  | 'programLicenseType'
  | 'programSeason'
  | 'programSubsidiesAccepted'
  | 'programType'
  | 'programYears'
  | 'scholarshipStatus'
  | 'siteType'

export interface SugarOption {
  label: string
  value: string
}

export interface SugarMe {
  options: Record<SugarOptionName, SugarOption[]>
  profile: SugarProfile
  sites: SugarSite[]
}

export enum SugarPaymentSetupType {
  ACH = 'ach',
  W9 = 'w9'
}

export enum SugarPaymentSetupLlcClassification {
  C = 'c',
  S = 's',
  P = 'p'
}

export enum SugarPaymentSetupTinType {
  EIN = 'ein',
  SSN = 'ssn'
}

export enum SugarPaymentSetupTaxClassificationCategory {
  INDIVIDUAL = 'individual',
  C = 'c',
  S = 's',
  PARTNERSHIP = 'partnership',
  TRUST = 'trust',
  LLC = 'llc',
  OTHER = 'other'
}

export interface SugarPaymentSetupInput {
  authorizationAcknowledgement: boolean
  file?: File | undefined
  nameAuthorizedSigner: string
  paymentSetupType: SugarPaymentSetupType
  titleAuthorizedSigner: string
}

export interface SugarPaymentSetupACHInput extends SugarPaymentSetupInput {
  bankAccountNumber: string
  bankAccountType: string
  bankName: string
  bankRoutingNumber: string
  nameOfAccount: string
  paymentSetupType: SugarPaymentSetupType.ACH
}

export interface SugarPaymentSetupW9Input extends SugarPaymentSetupInput {
  llcClassification: SugarPaymentSetupLlcClassification
  paymentSetupType: SugarPaymentSetupType.W9
  taxAddress: {
    city: string
    postalCode: string
    state: string
    street: string
  }
  taxClassificationCategory: SugarPaymentSetupTaxClassificationCategory
  taxIdentificationNumber: string
  taxName: string
  tinType: SugarPaymentSetupTinType
}

export interface SugarApplicationLetter {
  id: string
  type: string
  date: string
}

export interface SugarApplication {
  approvalDate: string | null
  id: string
  letter: SugarApplicationLetter | null
  // Nullable in two ways the backend does not reconcile: nestRosterItem emits
  // `$item['applicationStatus'] ?? null`, and a cleared Sugar enum persists as
  // '' rather than NULL -- the same trap MIQ-2684 fixed for gapStatus, which
  // this field never got. Consumers must treat '' and null identically.
  status: string | null
  statusDetails: string | null
}

export interface SugarFamilyLetter {
  applicationId: string
  childFirstName: string
  childLastName: string
  letterType: string
  letterDate: string | null
  downloadId: string
}

export type SugarFamilyLettersParams = {
  programYearId?: string
  siteId: string
}

export interface SugarKnowledgeBaseDocument {
  id: string
  documentName: string
  description: string | null
  documentDate: string | null
  downloadId: string
  category: string
  programYearId: string
  programYearName: string | null
}

export type SugarKnowledgeBaseParams = {
  programYearId?: string
}

export type SugarAttendanceGateReason = 'tuition_rates_incomplete' | 'gate_unavailable'

export interface SugarAttendanceSheet {
  // Only locked, periodId and siteId are always populated. GET never creates a
  // sheet row, so id is null until one exists; the rest are null on a
  // gate_unavailable fail-closed response (see the gatedPeriodUnavailable example).
  description: string | null
  id: string | null
  locked: boolean
  name: string | null
  numberOfDppKids: number | null
  numberOfKids: number | null
  periodId: string
  siteId: string
  // Optional even though the API marks them required, because the client must
  // work against backends deployed before MIQ-2421. An absent attendanceGated
  // signals fallback to the site-level publishedTuitionRatesComplete flag. Only
  // attendanceGated === true means the sheet is gated.
  //
  // This interface is deliberately narrower than the contract. The sheet also
  // carries programYearId, tuitionRatesGated and tuitionRatesComplete, left out
  // until something reads them: their per-program-year twins live in the ['me']
  // cache at a 1h staleTime against this sheet's 5m, so a reader has to choose a
  // source rather than inherit one. Add a field here when a consumer needs it.
  attendanceGated?: boolean
  gateMessage?: string | null
  gateReason?: SugarAttendanceGateReason | null
}

// MIQ-2641 — per-entry Year 1 B&B funding, dpp-sugarcrm PR #730 (merged to
// develop 2026-09-10, so the server validates and enforces these).
// The four enum-ish fields are typed `string | null`, NOT closed unions: a legacy
// row may hold any value a previous system stored, and the read returns it exactly
// as stored so the client can echo it back. The write path diffs before it
// validates, so an echoed legacy value is a no-op rather than a rejection.
export interface SugarAttendanceFunding {
  ccapEnrolled: string | null
  headStart: string | null
  upkType: string | null
  sfpEnrolled: string | null
  otherFunding: number | null
  familyOutOfPocket: number | null
  publishedTuitionRate: number | null
  requestedCap: number | null
  capRequested: boolean
  // MIQ-2920 (dpp-sugarcrm PR #762): a display hint, the Cap Request the same
  // enrollment carried in its most recent earlier period of the same program year,
  // when above zero, and that period's display name. Both null together when there
  // is no hint. Always present from #762 on; optional here because an environment
  // that predates it omits them, and their absence must change nothing. Read only:
  // never a prefill, never posted.
  priorRequestedCap?: number | null
  priorRequestedCapPeriod?: string | null
  // Per PERIOD here (site toggle AND effective date on or before the period end),
  // unlike the roster's toggle-only flags. Never re-derived on the client.
  headStartVisible: boolean
  sfpVisible: boolean
}

// MIQ-2641 (ii) — dpp-sugarcrm PR #734 (merged to develop 2026-09-10). Display
// only: the GET persists nothing, so these values are lost unless the provider
// submits them.
export interface SugarAttendanceFundingPrefill {
  source: 'prior_period' | 'roster'
  sourcePeriodId: string | null
  sourcePeriodName: string | null
  fields: string[]
}

// Write shape. Every key optional: an omitted key preserves the stored value.
export interface SugarAttendanceFundingWrite {
  ccapEnrolled?: string | null
  headStart?: string | null
  upkType?: string | null
  sfpEnrolled?: string | null
  otherFunding?: number | null
  familyOutOfPocket?: number | null
  publishedTuitionRate?: number | null
  requestedCap?: number | null
}

export interface SugarAttendanceEntry {
  daysAttended: number | null
  daysScheduled: number | null
  description: string | null
  earlyEndDate: string | null
  // Null until an entry row exists: the GET never creates one, and PR #734's
  // prefill leaves it null on an unconfirmed row.
  id: string | null
  isDppFamilyPayingZero: 'yes' | 'no' | null
  lateStartDate: string | null
  rcvdOtherTuitionSupport: 'yes' | 'no' | null
  upkParticipant: 'yes' | 'no' | null
  // Optional on purpose, and it stays that way. Its ABSENCE is the compatibility
  // signal: an item posted with no funding object is exempt from the server's
  // required rule, so a sheet from an environment that predates PR #730 behaves
  // exactly as this page did before. PR #730 is on dpp-sugarcrm develop as of
  // 2026-09-10, so anywhere current this is populated and the rule is enforced.
  funding?: SugarAttendanceFunding
  fundingPrefill?: SugarAttendanceFundingPrefill | null
}

export interface SugarAttendanceRules {
  attendancePeriodEndDateOverride: string | null
  attendancePeriodStartDateOverride: string | null
  disableEarlyEnd: boolean
  disableLateStart: boolean
  earlyEndDateIsEnforced: boolean
  earlyEndMax: string
  earlyEndMin: string
  lateStartDateIsEnforced: boolean
  lateStartMax: string
  lateStartMin: string
  maximumAllowedDays: number
}

export interface SugarAttendanceItem {
  contact: SugarContact
  enrollment: SugarEnrollment
  entry: SugarAttendanceEntry
  rules: SugarAttendanceRules
}

export type SugarAttendanceParams = {
  periodId: string
  programYearId: string
  siteId: string
}

export interface SugarAttendance {
  entries: SugarAttendanceItem[]
  sheet: SugarAttendanceSheet
}

export interface SugarAttendanceUpsertEntry {
  enrollmentId: string | null
  entry: {
    daysScheduled: number
    daysAttended: number
    description: string | null
    earlyEndDate: string | null
    // Null is accepted and takes the same server branch as an omitted key. The sheet never sends it.
    funding?: SugarAttendanceFundingWrite | null
    // Optional since MIQ-2954, and the difference matters on the wire: an omitted
    // key preserves the stored answer, a null clears it. The sheet omits it on any
    // 2026/27+ row that does not show the control, so a Pending row's earlier
    // answer is left alone rather than wiped.
    isDppFamilyPayingZero?: 'yes' | 'no' | null
    lateStartDate: string | null
    receivedOtherTuitionSupport: 'yes' | 'no' | null
    upkParticipant: 'yes' | 'no' | null
  }
}

export interface SugarAttendanceUpsert {
  description: string
  entries: SugarAttendanceUpsertEntry[]
  numberOfDppKids: number
  numberOfKids: number
  periodId: string
  programYearId: string
  siteId: string
}

export interface SugarContact {
  id: string
  // "Last, First" as one string. Split into the two fields below by MIQ-2830;
  // until that ships this is the only form the name arrives in.
  name: string
  // MIQ-2830. Optional because the roster payload does not carry them yet --
  // the CSV export reads them and emits a blank cell until it does. Drop the
  // question marks once the backend lands; nothing else has to change.
  firstName?: string
  lastName?: string
}

export type SugarContent = {
  bulletin: {
    content: string
    id: string
    label: string
    link: null
    type: 'bulletin'
  }[]
  link: {
    content: string | null
    id: string
    label: string
    link: string
    type: 'link'
  }[]
}

export interface SugarEnrollment {
  // MIQ-2850: SugarCRM's Scholarship Cap on the enrollment -- it caps the
  // scholarship, where tuitionCap below caps the tuition credit. The key keeps
  // the field's older internal name; the sheet labels it "Scholarship Cap"
  // (MIQ-2917). A Sugar currency string ("950.000000") or null. Optional because
  // only the attendance response carries it; the roster response has no cap
  // fields at all.
  dppTuitionCap?: string | null
  editable: boolean
  endDate: string | null
  // Optional only until the MIQ-2535 backend ships; the roster omits it today.
  funding?: SugarEnrollmentFunding
  // dpp-sugarcrm#665 is merged and deployed, so the roster carries both of these
  // on every row. Recorded because the failure mode if an environment is ever
  // behind on dpp-sugarcrm is silent rather than loud: the scholarship column
  // renders blank on every row and the confirmation banner never appears.
  // Nothing errors.
  //
  // Null on the attendance endpoint for every status but active (MIQ-2952); the
  // roster still returns the stored award for every status. One shared type, so
  // it carries the wider of the two.
  gapAwardAmount: number | null
  gapScholarshipStatus: 'Yes' | 'No' | 'Pending' | null
  // The MIQ-2540 backend shipped, so the roster carries this on every row -- the
  // comment above it claimed otherwise long after that stopped being true, which
  // MIQ-2830 called out. Null is a real value here (a scholarship with no
  // sub-status set); an absent key is not, and consumers tolerate one anyway.
  gapScholarshipSubStatus: string | null
  // Null for an enrollment with no scholarship record at all, which is not a
  // state and renders nothing.
  gapStatus: string | null
  id: string
  name: string
  participationType: string
  participationTypeDisplay: string
  pending: boolean
  programYear: string
  programYearId: string
  requestedEndDate: string | null
  requestedStartDate: string | null
  startDate: string
  status: string
  statusDisplay: string
  // MIQ-2850: the student/site tuition cap. Same representation and the same
  // attendance-only caveat as dppTuitionCap above.
  tuitionCap?: string | null
  // Null on the attendance response when the enrollment has no credit recorded;
  // the roster returns the same column and never nulls it in practice.
  tuitionCredit: string | null
}

export interface SugarEnrollmentCreate {
  parentEmail: string
  parentFirstName: string
  parentLastName: string
  parentPhone: string
  participationType: string
  programYearId: string
  siteId: string
  startDate: string
  studentDob: string
  studentFirstName: string
  studentLastName: string
}

export interface SugarEnrollmentUpdate {
  endDate: string | null
  enrollmentId: string
  participationType: string
  participationTypeChangeDate: string | null
  programYearId: string
  siteId: string
  startDate: string
}

export interface SugarEnrollmentItem {
  application: SugarApplication
  contact: SugarContact
  enrollment: SugarEnrollment
  site: SugarSite
}

// MIQ-2611 — per-student roster funding streams. Values are opaque wire tokens
// echoed to/from the backend; only src/lib/funding.ts encodes them.
export interface SugarEnrollmentFunding {
  ccapEnrolled: string | null
  headStart: string | null
  sfpEnrolled: string | null
  upkType: string | null
  headStartVisible: boolean
  sfpVisible: boolean
}

// Every key is optional: the server only validates and persists the keys that
// are present. headStart and sfpEnrolled are refused outright on a site without
// that stream — including when set to null — so omit rather than clear them.
export interface SugarRosterFundingValues {
  ccapEnrolled?: string | null
  headStart?: string | null
  sfpEnrolled?: string | null
  upkType?: string | null
}

export interface SugarRosterFundingItem {
  enrollmentId: string
  funding: SugarRosterFundingValues
}

export interface SugarRosterFundingUpsert {
  items: SugarRosterFundingItem[]
}

// Per-item batch outcome, returned in request order. A rejected item never
// aborts the batch. `enrollmentId` is null when the submitted item was
// malformed, and `message` is present only when `saved` is false.
export interface SugarRosterFundingResult {
  enrollmentId: string | null
  saved: boolean
  message?: string
}

export type SugarEnrollmentsParams = {
  programYearId: string
  siteId: string
}

export interface SugarOperatingDay {
  dayIndex: number
  startTime: string
  endTime: string
}

export interface SugarPaymentReport {
  docCreated: string
  docId: string
  docName: string
  id: string
}

export type SugarPaymentReportsParams = {
  siteId: string
}

export interface SugarPeriod {
  id: string
  name: string
  startDate: string
}

export interface SugarProgramYear {
  attendance: boolean
  endDate: string
  id: string
  letters: boolean
  name: string
  roster: boolean
  periods: SugarPeriod[]
  startDate: string
}

export interface SugarSocialMedia {
  facebook: string
  instagram: string
  linkedin: string
  x: string
}

export interface SugarProviderProfile {
  address: string
  addressCity: string
  addressState: string
  addressStreet1: string
  addressStreet2: string
  addressZip: string
  agesServed: string
  contactEmail: string
  contactFirstName: string
  contactLastName: string
  contactPhone: string
  curriculum: string[]
  directorName: string
  financialAssistanceAvailable: string[]
  languagesSpoken: string[]
  lastUpdated: string
  licenseName: string
  licenseNumber: string
  licenseType: string
  operatingDays: SugarOperatingDay[]
  participationLevels: string
  programDescription: string
  programName: string
  programSeason: string
  qualityRating: number
  qualityRatingTuitionCredit: number
  socialMedia: SugarSocialMedia
  website: string
}

export type SugarProviderProfileParams = {
  siteId: string
}

export type SugarProviderProfileUpdate = Pick<
  SugarProviderProfile,
  | 'contactEmail'
  | 'contactFirstName'
  | 'contactLastName'
  | 'contactPhone'
  | 'curriculum'
  | 'financialAssistanceAvailable'
  | 'languagesSpoken'
  | 'operatingDays'
  | 'programDescription'
  | 'programSeason'
  | 'website'
> &
  SugarProviderProfileParams & {
    socialMedia?: Partial<SugarSocialMedia>
  }

export interface SugarTuitionRateCohort {
  maxMonthlyRate: number
}

export interface SugarPublishedTuitionRate {
  id: string
  siteId: string
  programYearId: string
  effectiveDate: string
  fourYearOld: SugarTuitionRateCohort
  threeYearOld: SugarTuitionRateCohort | null
  ratesDifferThreeYr: boolean
  scholarshipOptIn: boolean | null
  // MIQ-2577. Per PROGRAM YEAR, not per site: these describe this record's own
  // programYearId. A site can be locked for 2026/2027 while 2027/2028 is still
  // open. Server-computed from the site's NEWEST row for that year, so on an
  // older row the flag can legitimately disagree with that row's own
  // scholarshipOptIn -- older rows are audit history, never lock fallbacks.
  scholarshipOptInLocked: boolean
  scholarshipOptInLockedOn: string | null
  dateModified: string
}

// MIQ-2672. Only the no-effectiveDate GET returns these. A POST response, an
// explicit-effectiveDate GET, a /history item, and the nested `upcoming` record
// itself are all the plain SugarPublishedTuitionRate shape -- which is why this
// is a separate interface rather than two more optional fields on the base.
export interface SugarPublishedTuitionRateCurrent extends SugarPublishedTuitionRate {
  // False means nothing is in effect yet and this record was promoted from
  // `upcoming` so the caller is never wrongly told "no rates on file".
  inEffect: boolean
  // The earliest row effective strictly after today, or null when none is
  // queued. Optional rather than required: the backend documents the key as
  // always present with a literal null, but instructs callers to treat a
  // missing key as null defensively.
  upcoming?: SugarPublishedTuitionRate | null
}

export type SugarPublishedTuitionRatesParams = {
  siteId: string
  effectiveDate?: string
}

export interface SugarPublishedTuitionRatesUpsert {
  siteId: string
  effectiveDate: string
  fourYearOld: SugarTuitionRateCohort
  threeYearOld?: SugarTuitionRateCohort
  ratesDifferThreeYr: boolean
  scholarshipOptIn?: boolean
}

// MIQ-2104 — Provider Profile Photo types. Status strings match the API on the wire
// (mixed casing is intentional — display labels are mapped at the component layer).
export type SugarProviderPhotoStatus = 'Under Review' | 'Active' | 'invalid'

export interface SugarProviderPhoto {
  id: string
  siteId: string
  filename: string
  status: SugarProviderPhotoStatus
  uploadedAt: string | null
  attestedAt: string | null
}

export interface SugarProviderPhotoLimits {
  maxPhotosPerProfile: number
  currentActiveCount: number
  currentPendingCount: number
}

export interface SugarProviderPhotoList {
  photos: SugarProviderPhoto[]
  limits: SugarProviderPhotoLimits
}

export type SugarProviderPhotosParams = { siteId: string }

// DELETE returns a raw object, not wrapped in StandardResponse.
export interface SugarProviderPhotoDeleteResult {
  id: string
  removed?: boolean
  deleted?: boolean
  status: 'Removed' | 'Under Review' | 'invalid'
}

export type SugarAttendanceResponse = SugarResponse<SugarAttendance> & {
  pagination: SugarResponsePagination
}
export type SugarContentResponse = SugarResponse<SugarContent>
export type SugarMeResponse = SugarResponse<SugarMe>
export type SugarEnrollmentsResponse = SugarPaginatedResponse<SugarEnrollmentItem>
export type SugarEnrollmentCreateResponse = SugarResponse<SugarEnrollmentItem>
export type SugarEnrollmentUpdateResponse = SugarResponse<SugarEnrollmentItem>
// `success` is hard-coded true on this endpoint, even when every item was
// rejected — the per-item outcome in `data` is the only save result.
export type SugarRosterFundingResponse = SugarResponse<SugarRosterFundingResult[]>
export type SugarFamilyLettersResponse = SugarResponse<SugarFamilyLetter[]>
export type SugarKnowledgeBaseResponse = SugarResponse<SugarKnowledgeBaseDocument[]>
export type SugarPaymentReportsResponse = SugarPaginatedResponse<SugarPaymentReport>
export type SugarProviderProfileResponse = SugarResponse<SugarProviderProfile>
export type SugarProviderProfileUpdateResponse = SugarResponse<SugarProviderProfile>
export type SugarPublishedTuitionRatesResponse = SugarResponse<SugarPublishedTuitionRateCurrent | null>
export type SugarPublishedTuitionRatesUpsertResponse = SugarResponse<SugarPublishedTuitionRate>
export type SugarPublishedTuitionRatesHistoryResponse = SugarResponse<SugarPublishedTuitionRate[]>

export type SugarVerificationCategory = 'age_verification' | 'address_verification' | 'income_verification'

export type SugarUploadedDocumentsParams = {
  enrollmentId: string
  programYearId: string
}

export interface SugarUploadedDocument {
  id: string
  category: SugarVerificationCategory
  // dateUploaded is ISO 8601 — note: NOT `uploadDate` (per MIQ-2117 iter-11; Sugar's
  // formatResponse() ISO-conversion check is case-sensitive and only applies to keys
  // containing lowercase 'date').
  dateUploaded: string
  filename: string
  // Document review status (MIQ-2568), aliased from Sugar's built-in
  // `documents.status_id` (`document_status_dom`). Known values: 'Under Review'
  // (set on upload), 'Active' (accepted), 'invalid' / 'Removed' (rejected/withdrawn).
  // Deliberately `string`, not a union — `document_status_dom` is admin-editable, so
  // the value set is open and consumers must tolerate unrecognised values.
  //
  // Required per the backend contract, but absent at runtime against a Sugar instance
  // predating MIQ-2568. Resolve through getVerificationDocumentStatusDisplay(), which
  // tolerates absence — do not dereference this field directly.
  status: string
}

export interface SugarUploadedDocumentsResponse extends SugarResponse<SugarUploadedDocument[]> {
  // Sugar's shared queryAndFormatResponse() always emits a pagination block on
  // multi-result returns. V1 portal ignores the values; the parser MUST accept the
  // field. Marked optional to keep the type tolerant.
  pagination?: SugarResponsePagination
}

// POST /documents/upload returns VerificationDocumentUploadData — the same shape as a
// listing item minus `status`. MIQ-2568 added status to the listing response only, so
// deriving it here keeps the two in sync without claiming a field the upload never sends.
export type SugarUploadedDocumentResult = Omit<SugarUploadedDocument, 'status'>

export type SugarUploadDocumentResponse = SugarResponse<SugarUploadedDocumentResult>

export interface SugarScholarshipConfirmInput {
  enrollmentId: string
  familyChargedZero: boolean
  noOtherFundingExceptUpk: boolean
}

export interface SugarScholarshipConfirmResult {
  enrollmentId: string
  status: string
  statusLabel: string
  awardAmount: number
  confirmedAt: string | null
  isLocked: boolean
  isConfirmable: boolean
}

// The incomplete-funding rejection is the one rejection that answers with a
// data object, and it carries the unanswered wire keys and nothing else. Kept
// as its own shape rather than optional fields on the result above: the two
// never co-occur, and optionals there would imply a confirm result can arrive
// without a status.
export interface SugarScholarshipConfirmMissingFields {
  missingFields: string[]
}

// Three data shapes, and `success` is the discriminator: the result when true;
// { missingFields } on the incomplete-funding rejection; null on every other
// rejection, the unresolvable-site one included -- which is why an absent
// missingFields means "no field list available", never "nothing is missing".
// A non-null `data` on its own does NOT mean the confirm succeeded, so callers
// narrow on success and then on the shape.
export type SugarScholarshipConfirmResponse = SugarResponse<
  SugarScholarshipConfirmResult | SugarScholarshipConfirmMissingFields | null
>
